import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useInterval } from "../../utils/useInterval";

// Data for the home page side widgets and the post view counter, with the
// same requests as Ekloges.jsx.

// Only an auth failure sends the user to login; other errors are logged.
export const useLoginRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (err) => {
      if (axios.isCancel(err)) return;
      console.error(err);
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        navigate("/login", { state: { from: location }, replace: true });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
};

// Hearing/deadline dates for the month calendar and the webcal feed id.
export function useSmallCalendar() {
  const axiosPrivate = useAxiosPrivate();
  const onError = useLoginRedirect();
  const [dates, setDates] = useState([]);
  const [calUuid, setCalUuid] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    axiosPrivate
      .get("/calendar/small", { signal: controller.signal })
      .then((response) => {
        setDates(Array.isArray(response.data?.small) ? response.data.small : []);
        setCalUuid(response.data?.calUuid?.uuid ?? null);
      })
      .catch(onError);
    return () => controller.abort();
  }, [axiosPrivate, onError]);

  return { dates, calUuid };
}

// The Word report: created on demand, then offered as a download.
export function useReport() {
  const axiosPrivate = useAxiosPrivate();
  const onError = useLoginRedirect();
  const [reportUUID, setReportUUID] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const createReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const response = await axiosPrivate.get("/report");
      setReportUUID(response.data.uuid);
    } catch (err) {
      onError(err);
    } finally {
      setReportLoading(false);
    }
  }, [axiosPrivate, onError]);

  return { reportUUID, reportLoading, createReport };
}

const VIEW_RETRY_PAUSE_MS = 5 * 60 * 1000;

// Post components record views in localStorage ("post-<uuid>"); this sends
// the unsent ones every second, like Ekloges.jsx. After a failure it pauses
// for a few minutes instead of retrying every second.
export function usePostViewCounter() {
  const axiosPrivate = useAxiosPrivate();
  const sendingRef = useRef(false);
  const pausedUntilRef = useRef(0);

  useInterval(async () => {
    if (sendingRef.current || Date.now() < pausedUntilRef.current) return;
    const pending = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith("post-")) continue;
      const item = localStorage.getItem(key);
      if (item !== "sent") pending.push({ [key]: item });
    }
    if (!pending.length) return;
    sendingRef.current = true;
    try {
      await axiosPrivate.post("/viewCounter", { viewCounts: pending });
      pending.forEach((entry) => localStorage.setItem(Object.keys(entry)[0], "sent"));
    } catch (err) {
      console.error(err);
      pausedUntilRef.current = Date.now() + VIEW_RETRY_PAUSE_MS;
    } finally {
      sendingRef.current = false;
    }
  }, 1000);
}
